/**
 * 管理番号の採番(要件定義書v3.5 §5)。
 *
 * 体系: 事業部 D-xx / 科目 A-xxx / 支払い先 V-xxxx / 階層 H-xxxx /
 *       経費申請 K-YYYY-連番 / 請求書 I-YYYY-連番 / 固定費自動 F-YYYY-連番
 *
 * counters/{key} を Firestore トランザクションでインクリメントするため、
 * 同時申請でも番号は重複しない。プルダウンには表示せず、一覧・帳票・フィルタで使う。
 */
import { doc, runTransaction } from 'firebase/firestore';
import { getDb } from './firebase';

type SeqPrefix = 'K' | 'I' | 'F';
type MasterPrefix = 'V';

function pad(n: number, width: number): string {
  return String(n).padStart(width, '0');
}

/** 年別の取引番号(K-2026-0001 など)を採番する */
export async function nextTransactionCode(prefix: SeqPrefix, year: number): Promise<string> {
  const key = `${prefix}-${year}`;
  const seq = await nextSequence(key);
  return `${prefix}-${year}-${pad(seq, 4)}`;
}

/**
 * 経費の管理番号(2026-08-26 新ルール): 会社-項目-科目略称-YYYY-MM-#### 。
 * 連番は「会社×科目×年月」の組合せごとに 0001 から採番する(例: KMA-販管-消耗品-2026-08-0001)
 */
export async function nextExpenseCode(parts: {
  company: string;
  item: string;
  abbr: string;
  year: number;
  month: number;
}): Promise<string> {
  const prefix = `${parts.company}-${parts.item}-${parts.abbr}-${parts.year}-${String(parts.month).padStart(2, '0')}`;
  const seq = await nextSequence(prefix);
  return `${prefix}-${pad(seq, 4)}`;
}

/** マスタ番号(V-0001 など)を採番する */
export async function nextMasterCode(prefix: MasterPrefix): Promise<string> {
  const seq = await nextSequence(prefix);
  return `${prefix}-${pad(seq, 4)}`;
}

async function nextSequence(key: string): Promise<number> {
  const ref = doc(getDb(), 'counters', key);
  return runTransaction(getDb(), async (tx) => {
    const snap = await tx.get(ref);
    const next = ((snap.data()?.value as number | undefined) ?? 0) + 1;
    tx.set(ref, { value: next }, { merge: true });
    return next;
  });
}
